import { CommonModule } from "@angular/common";
import {
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewChild,
  ViewEncapsulation,
  NgZone, AfterViewInit,
} from "@angular/core";
import { FormControl, ReactiveFormsModule, Validators } from "@angular/forms";
import { MatAutocompleteModule, MatAutocompleteTrigger } from "@angular/material/autocomplete";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { MatTooltipModule } from "@angular/material/tooltip";
import { Observable, Subject, map, startWith } from "rxjs";
import { scaleInOutAnimation } from "src/app/shared/animations/scale-in-out.animation";
import { IconComponent } from "../../../icon/icon.component";
import { modeType } from "src/app/shared/interfaces/inputype.interface";
import { getDateInMomentFormatSinceToday, setInvalidOptionInSelectComponent } from "./select-functions";
import { configurations } from "src/app/shared/interfaces/configurations.interface";
import { STipoConfiguracion } from "./select-interface";
import { Icon } from "src/app/shared/interfaces/icon.interface";

interface Option {
  icon?: Icon;
  sColor?: string;
  sCodigo: string;
  bVisible?: boolean;
  sDescripcion: string;
  sComentario?: string;
  bIsInvalidOption?: boolean;
}

@Component({
  selector: "mg-input-select",
  templateUrl: "./select.component.html",
  styleUrls: ["./select.component.scss"],
  encapsulation: ViewEncapsulation.None,
  animations: [scaleInOutAnimation],
  standalone: true,
  imports: [
    CommonModule,
    MatAutocompleteModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatTooltipModule,
    IconComponent
  ]
})
export class SelectComponent implements OnInit, OnChanges, OnDestroy, AfterViewInit {
  @Input() hint = false;
  @Input() hintPosition?: 'down' | 'right' = 'down';
  @Input() hintmessage = "";
  @Input() inputControl: FormControl = new FormControl(null);
  @Input() label = "";
  @Input() placeholder = "";
  @Input() aOpciones?: any;
  @Input() required = false;
  @Input() mode: modeType;
  @Input() isLoading = false;
  @Input() withLabel = true;
  @Input() hint_active = false;
  @Input() hint_message = "";
  @Input() firstValue?: boolean = false;
  @Input() blurOn?: boolean = true;
  @Input() optionIcon = false;
  @Input() isSmallScreen = (window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth) <= 740;
  @Input() taskListPerUser: any = null;
  @Input() selectedRowTask: any = null;
  @Input() lastItemSelected?: any = null;
  @Input() disableValidatios = false;

  public sCodigo_Categoria: any = null;
  public sError_Message = '';

  opcionesfiltradas: Observable<Option[]>;

  public firstDate = "";
  public lastDate = "";

  private readonly destroy$ = new Subject<void>();

  @ViewChild("myInputToBlur", { read: MatAutocompleteTrigger }) trigger: MatAutocompleteTrigger;
  @ViewChild("myInputToBlur") private readonly myInputRef: ElementRef;

  constructor(
    private readonly el: ElementRef,
    private readonly ngZone: NgZone,
  ) { }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['aOpciones']) this.handleOpcionesChange(changes);
    if (changes['mode']) this.initMode();
    if (changes['required']) this.setValidators();
    if (changes['blurOn'] && this.blurOn === false) this.myInputRef.nativeElement.blur();
  }

  private handleOpcionesChange(changes: SimpleChanges): void {
    if (changes['aOpciones'].previousValue !== undefined) {
      this.refreshOpciones();
    }
    if (changes['lastItemSelected']) {
      this.sCodigo_Categoria = changes['lastItemSelected']['currentValue'] ?? null;
    }
    if (changes['aOpciones'].currentValue !== undefined) {
      this.refreshOpciones();
      if (this.sCodigo_Categoria) {
        this.inputControl.patchValue(this.sCodigo_Categoria);
      }
      this.inputControl.updateValueAndValidity();
    }
    if (this.firstValue && this.aOpciones.length > 0) {
      this.inputControl.setValue(this.aOpciones[0].sCodigo);
    }
  }

  ngOnInit(): void {
    this.initMode();
  }

  ngAfterViewInit(): void {
    this.setupScrollListeners();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private refreshOpciones(): void {
    this.opcionesfiltradas = this.inputControl.valueChanges.pipe(
      startWith(''),
      map(value => this._filter(value || '', this.aOpciones)),
    );
  }

  setupScrollListeners(): void {
    this.ngZone.runOutsideAngular(() => {
      const close = (event: Event) => {
        const panel = document.querySelector('.mat-mdc-autocomplete-panel');
        if (panel?.contains(event.target as Node)) return;
        this.ngZone.run(() => {
          if (this.trigger?.panelOpen) this.trigger.closePanel();
        });
      };
      document.addEventListener('scroll', close, { passive: true, capture: true });
      this.destroy$.subscribe(() => {
        document.removeEventListener('scroll', close, { capture: true });
      });
    });
  }

  private initMode(): void {
    this.setValidators();
    this.refreshOpciones();
    this.inputControl.valueChanges.subscribe((value) => {
      if (value && this.aOpciones) {
        let options = this.aOpciones;
        if (
          this.aOpciones.length > 0 &&
          this.taskListPerUser &&
          this.selectedRowTask &&
          this.aOpciones.some((option: configurations) => option.sTipo_Configuracion === STipoConfiguracion.TipoHora)
        ) {
          if (this.taskListPerUser.length > 0 && this.selectedRowTask.value.dFecha_Registro) {
            const selectedTask: any = this.taskListPerUser.find(
              (task: any) => task.dDate === this.selectedRowTask.value.dFecha_Registro.format("YYYY-MM-DD")
            );
            options = setInvalidOptionInSelectComponent(
              this.aOpciones,
              this.taskListPerUser.map((x: any) => ({
                sEstado: x.sEstado || "",
                dDate: x.dDate || getDateInMomentFormatSinceToday(-1),
                dRegistrationDate: selectedTask ? selectedTask.dDate : getDateInMomentFormatSinceToday(-1),
                nReadyMark: x.nReadyMark || 0,
              })),
            );
          }
        }
        this.checkOption(this.inputControl.value, options);
      }
    });
    if (this.mode === 'read') {
      this.inputControl.disable();
    } else {
      this.inputControl.enable();
    }
  }

  setValidators(): void {
    if (this.required) {
      this.inputControl.setValidators([Validators.required]);
    } else {
      this.inputControl.clearValidators();
    }
    this.inputControl.updateValueAndValidity();
  }

  mostrarDropdrown(sCodigo: string): string {
    if (!sCodigo) return '';
    const option = this.aOpciones.find((option: Option) => option.sCodigo === sCodigo);
    return option?.sDescripcion ?? '';
  }

  private _filter(value: any, aOpciones: Option[]): Option[] {
    let filterValue = "";
    if (typeof value === "string") {
      filterValue = value.toLowerCase();
    }
    if (typeof value === "object") {
      filterValue = value.sDescripcion.toLowerCase();
    }
    if (aOpciones?.length > 0) {
      this.placeholder = this.label;
      return aOpciones
        .filter((option: Option) => option.sDescripcion.toLowerCase().includes(filterValue))
        .filter((x) => x.bVisible === true || x.bVisible === undefined);
    }
    this.placeholder = "El listado está vacio";
    return [];
  }

  private checkOption(value: string, aOpciones: Option[]): void {
    const aCodigos = aOpciones.map((opcion: Option) => opcion.sCodigo);
    const isValid = aCodigos.includes(value);
    const opcion: any = aOpciones.find((x: Option) => x.sCodigo === value) ?? {};
    if (isValid) {
      if (this.inputControl.value === value && opcion.bIsInvalidOption) {
        this.sError_Message = opcion.sMessage;
        this.inputControl.setErrors({ bIsInvalidOption: true });
      } else {
        this.inputControl.setErrors(null);
      }
    } else if (this.required) this.inputControl.setErrors({ invalid: true });
  }

  get IconOnInput() {
    if (this.inputControl.valid || this.inputControl.disabled) {
      const iconPpal = this.aOpciones.find((opt: Option) => opt.sCodigo === this.inputControl.value);
      return iconPpal?.icon ?? false;
    }
    return false;
  }
}